import { useEffect, useState } from 'react'
import { api, mutate } from '@/runtime'
import { Button, Sheet, cx } from '@/components/ui'
import { useUi } from '@/state/ui'
import { tokens } from '@/lib/colour'
import type { PageResource } from '@/kinds/page/apply'
import {
  foldDiff,
  isSame,
  lineDiff,
  versionLabel,
  type PageVersion,
  type PageVersionFull,
} from '@/lib/pageHistory'

// the diff's two sides from the hue tokens, mixed with the page's own text colour so
// they read on light and dark alike (light-dark() followed the wrong scheme in a sheet)
const ink = (hue: 'emerald' | 'rose') => `color-mix(in oklab, ${tokens(hue).stripe} 55%, var(--color-fg))`
const TONE = {
  add: { color: ink('emerald'), background: tokens('emerald').soft },
  del: { color: ink('rose'), background: tokens('rose').soft },
}

/**
 * A page's history: its versions from the server's changelog (online only,
 * read on opening), each viewable read-only against the page as it is now, and
 * restorable as an ordinary patch (so it is a new version and undoable).
 */
export const HistorySheet = ({ page, onClose }: { page: PageResource; onClose: () => void }) => {
  const [versions, setVersions] = useState<PageVersion[] | null>(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useState<PageVersionFull | null>(null)
  const flash = useUi((s) => s.showFlash)

  useEffect(() => {
    let stale = false
    api
      .pageHistory(page.id)
      .then((r) => !stale && setVersions(r.versions))
      .catch((e: Error) => !stale && setError(navigator.onLine ? e.message : 'History needs a connection.'))
    return () => {
      stale = true
    }
  }, [page.id])

  const show = async (v: PageVersion) => {
    setError('')
    try {
      setOpen(await api.pageVersion(page.id, v.seq))
    } catch (e) {
      setError(navigator.onLine ? (e as Error).message : 'History needs a connection.')
    }
  }

  const restore = async () => {
    if (!open) return
    await mutate.patch(page.id, { spec: { text: open.text, blocks: open.blocks ?? [] } })
    flash('Restored', 3000)
    onClose()
  }

  const diff = open ? lineDiff(page.spec.text, open.text) : []
  const rows = foldDiff(diff)

  return (
    <Sheet
      open
      onClose={onClose}
      title={open ? versionLabel(open) : 'History'}
      footer={
        open ? (
          <div className="flex gap-2">
            <Button variant="chip" onClick={() => setOpen(null)} data-testid="history-back">
              Back
            </Button>
            <Button
              variant="chipOn"
              onClick={() => void restore()}
              disabled={isSame(diff) && JSON.stringify(open.blocks) === JSON.stringify(page.spec.blocks)}
              data-testid="history-restore"
            >
              Restore this version
            </Button>
          </div>
        ) : undefined
      }
    >
      {error && <p className="text-sm text-muted">{error}</p>}
      {!open && versions === null && !error && (
        <div className="flex flex-col gap-2" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-11 rounded-lg bg-raised animate-pulse" />
          ))}
        </div>
      )}
      {!open && versions && versions.length === 0 && (
        <p className="text-sm text-muted">No earlier versions.</p>
      )}
      {!open && versions && (
        <ul className="group-list" data-testid="history-list">
          {versions.map((v) => (
            <li key={v.seq}>
              <button
                type="button"
                className="w-full text-left px-1 py-2.5 min-h-11"
                onClick={() => void show(v)}
                data-testid="history-version"
              >
                <span className="text-sm font-medium">{versionLabel(v)}</span>
                {v.current && <span className="ml-2 text-xs text-faint">current</span>}
                <span className="block text-xs text-muted truncate">{v.preview || 'Empty'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <div data-testid="history-diff">
          <p className="text-xs text-faint mb-2">
            Against the page now: <span style={{ color: ink('rose') }}>−</span> goes away,{' '}
            <span style={{ color: ink('emerald') }}>+</span> comes back.
          </p>
          {isSame(diff) ? (
            <p className="text-sm text-muted">The words are the same as now.</p>
          ) : (
            <pre className="text-xs leading-relaxed whitespace-pre-wrap break-words font-mono">
              {rows.map((r, i) =>
                r.op === 'gap' ? (
                  <span key={i} className="block text-faint py-1">
                    {r.hidden} unchanged line{r.hidden === 1 ? '' : 's'}
                  </span>
                ) : (
                  <span
                    key={i}
                    className={cx('block', r.op === 'del' && 'line-through', r.op === 'same' && 'text-muted')}
                    style={r.op === 'same' ? undefined : TONE[r.op]}
                    data-op={r.op}
                  >
                    {r.op === 'add' ? '+ ' : r.op === 'del' ? '− ' : '  '}
                    {r.text}
                  </span>
                ),
              )}
            </pre>
          )}
        </div>
      )}
    </Sheet>
  )
}
